import { ChevronLeft } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { t } from '@/lib/i18n';

/**
 * A chat on a phone (docs/features/mobile.md#chats): the whole screen, like a chat app — back and title on top, the
 * messages, the field to write in at the bottom, right above the keyboard. Desk chats, a Work Item's thread and a
 * view's chat open in it; on a larger screen they stay in their page.
 */
export function ChatScreen({
  title,
  subtitle,
  onBack,
  actions,
  toolbar,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onBack: () => void;
  /** Small buttons at the right of the title. */
  actions?: ReactNode;
  /** A slim row under the title (e.g. "Agent may write"). */
  toolbar?: ReactNode;
  /** The thread: it fills the rest and scrolls itself. */
  children: ReactNode;
}) {
  const viewport = useVisualViewport();
  useNoPageScroll();
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-x-0 top-0 z-50 flex flex-col bg-background"
      style={{ height: viewport.height, transform: `translateY(${viewport.offsetTop}px)` }}
    >
      <header className="shrink-0 border-b bg-background/95 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="flex h-14 items-center gap-1 pr-2 pl-1">
          <Button variant="ghost" size="icon" onClick={onBack} aria-label={t('Back')} className="size-11 shrink-0">
            <ChevronLeft className="size-6" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] leading-tight font-semibold">{title}</div>
            {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
        </div>
        {toolbar && <div className="flex items-center gap-2 overflow-x-auto px-3 pb-2">{toolbar}</div>}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * The part of the screen not covered by the keyboard. iOS keeps the page's height when the keyboard opens and moves
 * the visible part instead; a full-screen chat follows the visible part, so its field stays above the keyboard.
 */
function useVisualViewport() {
  const read = () => ({
    height: window.visualViewport?.height ?? window.innerHeight,
    offsetTop: window.visualViewport?.offsetTop ?? 0,
  });
  const [viewport, setViewport] = useState(read);
  useEffect(() => {
    const vv = window.visualViewport;
    const update = () => setViewport(read());
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return viewport;
}

/**
 * The page under the chat does not scroll along (iOS would move it when the field gets the focus), and is where it was
 * when the chat closes: iOS leaves the page moved up after its keyboard, so the app looked shifted afterwards.
 */
function useNoPageScroll() {
  useEffect(() => {
    const html = document.documentElement;
    const before = html.style.overflow;
    const scrollY = window.scrollY;
    html.style.overflow = 'hidden';
    return () => {
      (document.activeElement as HTMLElement | null)?.blur?.();
      html.style.overflow = before;
      window.scrollTo(0, scrollY);
    };
  }, []);
}
